/**
 * Der Abschnitt "Straßen-Sendung" (`ship-sendung` im Modus "road") - Geschwister von
 * `FlugSendung.tsx`, Asana-Ticket 1218176179911324 ("Zusaetzlicher LKW Speditionstyp"). Wie bei
 * Flug gibt es kein Suchfeld und keine Verknuepfung: alle drei Felder sind direkte Handeingaben,
 * jede einzeln per `onAendern` geschrieben, reine Handpflege ohne DSV-Anbindung. Bauplan wie
 * `FlugSendung.tsx`/`Referenzen.tsx`/`Transport.tsx`: lokales `patch(feld, wert)` aus
 * `bestellung`/`onAendern`, keine eigene Aufrufform.
 */
import type { Order } from "../../../lib/types.ts";

export interface RoadStatusOption {
  id: string;
  label: string;
}

/**
 * Die drei manuellen Zustaende einer Straßen-Sendung - dieselben drei Werte wie
 * `AIR_STATUS_OPTIONEN` (`FlugSendung.tsx`), aber bewusst NICHT von dort importiert und auch NICHT
 * in `config/shipping.ts` verlegt:
 *
 * - Ein `AIR_`-benannter Export fuer Straßenfracht waere irrefuehrend, und die beiden Listen haengen
 *   an zwei getrennten DB-CHECKs (`road_status` bzw. `air_status`), die unabhaengig voneinander
 *   wachsen koennen - eine geteilte Konstante wuerde diese Unabhaengigkeit verdecken.
 * - `config/shipping.ts` ist eine 1:1-Portierung aus `index.html` und wird von
 *   `test/reference/herkunft.test.ts` auf eine Herkunftsmarke je Wert-Export geprueft - ein neu
 *   erfundener Wert wie dieser hat keine und macht diesen Test deterministisch rot. Aus genau diesem
 *   Grund sitzt auch `AIR_STATUS_OPTIONEN` bereits in `FlugSendung.tsx` statt dort.
 */
export const ROAD_STATUS_OPTIONEN: readonly RoadStatusOption[] = [
  { id: "gebucht", label: "Gebucht" },
  { id: "unterwegs", label: "Unterwegs" },
  { id: "angekommen", label: "Angekommen" },
];

export interface StrassenSendungProps {
  bestellung: Order;
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
}

export function StrassenSendung({ bestellung: o, onAendern }: StrassenSendungProps) {
  const id = o.id as string;
  const patch = (feld: string, wert: unknown): void => onAendern(id, { [feld]: wert });

  const roadCarrier = o.roadCarrier ?? "";
  const roadCmrNo = o.roadCmrNo ?? "";
  const roadStatus = ROAD_STATUS_OPTIONEN.some((s) => s.id === o.roadStatus) ? o.roadStatus! : "";

  return (
    <div className="field-grid">
      <div className="field">
        <label>Spedition / Frachtführer</label>
        <input
          value={roadCarrier}
          placeholder="z. B. Dachser"
          onChange={(e) => patch("roadCarrier", e.currentTarget.value)}
        />
      </div>
      <div className="field">
        <label>CMR-/Frachtbrief-Nr.</label>
        <input
          className="mono"
          value={roadCmrNo}
          onChange={(e) => patch("roadCmrNo", e.currentTarget.value)}
        />
      </div>
      <div className="field">
        <label>Status</label>
        <select value={roadStatus} onChange={(e) => patch("roadStatus", e.currentTarget.value)}>
          <option value="" disabled>
            Bitte wählen
          </option>
          {ROAD_STATUS_OPTIONEN.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
